export default function MarkPilotPrivacy() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#0F766E]">
            Privacy Policy - MarkPilot Watermark Maker
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Effective date: <time dateTime="2026-07-21">July 21, 2026</time>
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <p>
            <strong>Cosmovex</strong> built <strong>MarkPilot</strong>, a photo
            watermarking app, as an ad-supported application. This Privacy
            Policy explains what information MarkPilot may process, how that
            information is used, and the choices you have when using the app.
          </p>

          <h2 className="text-2xl font-bold text-[#0F766E]">
            Information We Process
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Photos you watermark:</strong> photos you pick are
              loaded, watermarked, and exported entirely on your device. Your
              photos are never uploaded to our servers.
            </li>
            <li>
              <strong>Logo images:</strong> logo or signature images you use
              as watermarks are read locally and stay on your device.
            </li>
            <li>
              <strong>Watermark templates:</strong> the text, logo, position,
              and style presets you save are stored locally on your device.
            </li>
            <li>
              <strong>Exported files:</strong> watermarked photos are written
              to the location you pick in the system save dialog. They are not
              sent anywhere.
            </li>
            <li>
              <strong>App preferences:</strong> theme choice, language, and
              editor settings are stored locally on your device.
            </li>
            <li>
              <strong>Usage and ad data:</strong> if ads are enabled, AdMob or
              related Google Mobile Ads services may collect device and
              advertising data as described in Google&apos;s own privacy
              documentation.
            </li>
            <li>
              <strong>Analytics and diagnostics:</strong> Firebase Analytics
              and Firebase Crashlytics may collect app stability, usage
              patterns, and crash reports to improve reliability and user
              experience.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#0F766E]">
            How We Use Information
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              To let you add text and logo watermarks to photos on your
              device, offline.
            </li>
            <li>
              To batch-watermark multiple photos with a saved template.
            </li>
            <li>
              To export watermarked photos at full resolution to a location
              you choose.
            </li>
            <li>
              To remember your templates, settings, and preferences.
            </li>
            <li>
              To improve app performance, reliability, and product quality.
            </li>
            <li>
              To display ads in supported versions of the app, where
              applicable.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#0F766E]">Local Storage</h2>
          <p>
            MarkPilot is designed to keep your data on your device. Photos,
            logos, watermark templates, and preferences are stored locally and
            are not uploaded to any server. Data remains on your device until
            you remove it, clear the app&apos;s storage, or uninstall the app.
          </p>

          <h2 className="text-2xl font-bold text-[#0F766E]">Network Access</h2>
          <p>
            Watermarking works fully offline — the watermark engine is bundled
            with the app. The app makes network requests only for ads,
            Firebase analytics and crash reporting, and the rotating
            cross-promotion banner shown on the home screen. No photo bytes,
            logo images, or template data are ever transmitted.
          </p>

          <h2 className="text-2xl font-bold text-[#0F766E]">Advertising</h2>
          <p>
            MarkPilot may use <strong>Google AdMob</strong> to show banner,
            interstitial, and app-open ad formats. AdMob may collect and
            process identifiers, device information, approximate location, and
            ad interaction data to deliver and measure ads. You can remove ads
            via a one-time Pro upgrade.
          </p>

          <h2 className="text-2xl font-bold text-[#0F766E]">
            In-App Purchases
          </h2>
          <p>
            MarkPilot offers an optional one-time Pro upgrade that removes
            advertisements. Purchases are processed through Google Play and
            are subject to Google&apos;s terms and privacy policies.
          </p>

          <h2 className="text-2xl font-bold text-[#0F766E]">
            Third-Party Services
          </h2>
          <p>MarkPilot may rely on third-party services such as:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>Google Play Services</li>
            <li>Google AdMob</li>
            <li>Firebase Analytics</li>
            <li>Firebase Crashlytics</li>
            <li>Firebase Remote Config</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#0F766E]">Data Sharing</h2>
          <p>
            We do not sell your photos, templates, or personal data. Data may
            be shared only with service providers required to operate app
            features, such as advertising, analytics, app distribution, or
            diagnostics infrastructure.
          </p>

          <h2 className="text-2xl font-bold text-[#0F766E]">Data Retention</h2>
          <p>
            Local app data remains on your device until you remove it, clear
            the app&apos;s storage, or uninstall the app. Data collected by
            third-party services such as AdMob and Firebase is retained
            according to those providers&apos; own policies.
          </p>

          <h2 className="text-2xl font-bold text-[#0F766E]">
            Children&apos;s Privacy
          </h2>
          <p>
            MarkPilot is not intended for children under 13, and we do not
            knowingly collect personal information from children through the
            app.
          </p>

          <h2 className="text-2xl font-bold text-[#0F766E]">Security</h2>
          <p>
            We take reasonable steps to protect data processed by the app,
            but no method of storage or transmission is completely secure.
          </p>

          <h2 className="text-2xl font-bold text-[#0F766E]">Your Choices</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>You can delete templates and recent items from inside the app.</li>
            <li>
              You can uninstall the app to remove all locally stored data from
              your device.
            </li>
            <li>
              You can purchase the Pro upgrade to disable all advertising.
            </li>
            <li>
              You can manage ad personalization and advertising controls
              through your device and Google settings.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#0F766E]">
            Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. When we do,
            we will post the updated version on this page and revise the
            effective date.
          </p>

          <h2 className="text-2xl font-bold text-[#0F766E]">Contact Us</h2>
          <p>
            For privacy-related questions about MarkPilot, contact{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            .
          </p>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
